import { action, computed, flow, makeObservable, observable } from 'mobx';
import { logger } from '@sentry/utils';
import moment from 'moment';
import { AxiosRequestConfig } from 'axios';
import {
  OrdersRatingFilter,
  OrdersRatingItem,
  OrdersRatingSorting,
  defaultFilter,
  defaultPagination,
  ordersRatingCast,
  sortFieldsMap,
} from '../types';
import { findAllSelectedOption, skipAllSelectedOption } from '../helpers';
import { LoadingStatus, TPagination, paginationCast } from '~/types/main';
import { allOptionsValue } from '~/constants/main';
import { localeStore } from '~/mobx/localesStore';
import { userStore } from '~/mobx/userStore';
import { IPickerOption } from '~/view/components/Controls/Pickers/Picker/types';
import { downloadFile, extractFileName, getTranslate } from '~/utils/helpers';
import client from '~/services/client';
import wms from '~/services/wms';

export class OrdersRatingStore {
  constructor() {
    makeObservable(this);
  }

  @observable ordersRatingList: OrdersRatingItem[] = [];
  @observable loadingStatus = LoadingStatus.Init;
  @observable exportLoadingStatus = LoadingStatus.Init;

  private ordersRatingGettingTimeoutId: NodeJS.Timeout | undefined = undefined;
  private ordersRatingGetAbortController = new AbortController();
  private ordersExportAbortController = new AbortController();

  @observable filter: OrdersRatingFilter = defaultFilter;

  @observable sorting: OrdersRatingSorting = {
    sortField: undefined,
    sortDir: undefined,
  };

  @observable pagination: TPagination = defaultPagination;

  @computed get availableBranches() {
    return [
      { label: localeStore.t('reports_select_all_branches_dropdown'), value: allOptionsValue },
      ...userStore.allUserBranches.map(
        (branch): IPickerOption => ({
          label: getTranslate(branch.name),
          value: branch.id,
          warringText: branch.is_delete === 1 ? localeStore.t('branch_was_deleted') : undefined,
        })
      ),
    ];
  }

  @action private _getOrdersRating = flow(function* (this: OrdersRatingStore, page: number): any {
    // if only allBranches in available list
    if (this.availableBranches.length === 1) {
      return;
    }

    this.ordersRatingGetAbortController.abort();
    const ordersRatingGetAbortController = new AbortController();
    this.ordersRatingGetAbortController = ordersRatingGetAbortController;

    try {
      const params = this._makeFetchOrdersRatingParams(page);
      this.loadingStatus = LoadingStatus.Loading;

      const { data = [], ...pagination } = yield client.get('/v3/crm/orders-rating', { params, signal: this.ordersRatingGetAbortController.signal });

      this.pagination = paginationCast(pagination);
      this.ordersRatingList = ordersRatingCast(data);

      this.loadingStatus = LoadingStatus.Success;
    } catch (error) {
      if (ordersRatingGetAbortController.signal.aborted) {
        this.loadingStatus = LoadingStatus.Aborted;
      } else {
        this.loadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'ordersRatingStore _getOrdersRating' });
      }
    }
  }).bind(this);

  @action selectAllBranches = () => {
    this.filter.branches = this.availableBranches;
  };

  @action public getOrdersRating = (page?: number) => {
    if (this.loadingStatus === LoadingStatus.Init) {
      this._getOrdersRatingWithNoDelay(1);
    } else {
      this._getOrdersRatingWithNoDelay(page || 1);
    }
  };

  @action private _getOrdersRatingWithDelay = (pageNumber: number = 1) => {
    clearTimeout(this.ordersRatingGettingTimeoutId);

    this.ordersRatingGettingTimeoutId = setTimeout(() => {
      this._getOrdersRating(pageNumber);
    }, 700);
  };

  @action private _getOrdersRatingWithNoDelay = (pageNumber: number = 1) => {
    clearTimeout(this.ordersRatingGettingTimeoutId);
    this._getOrdersRating(pageNumber);
  };

  @action public resetSearch = () => {
    this.filter.search = '';
    this._getOrdersRatingWithNoDelay();
  };

  @action public resetDate = () => {
    this.filter.dateFrom = null;
    this.filter.dateTo = null;
    this._getOrdersRatingWithNoDelay();
  };

  @action private _makeFetchOrdersRatingParams = (page: number, output_format: 'json' | 'excel' | 'url_to_excel' = 'json') => {
    const params: { [key: string]: string | number | number[] } = {
      page,
      search: this.filter.search,
      branches:
        this.filter.branches.length === this.availableBranches.length
          ? []
          : skipAllSelectedOption(this.filter.branches).map(({ value }) => Number(value)),
      output_format,
    };

    if (this.sorting.sortDir && this.sorting.sortField) {
      params.sort_field = sortFieldsMap[this.sorting.sortField];
      params.sort_dir = this.sorting.sortDir;
    }

    if (this.filter.dateFrom && this.filter.dateTo) {
      params.from_date = moment(this.filter.dateFrom).format('YYYY-MM-DD');
      params.to_date = moment(this.filter.dateTo).format('YYYY-MM-DD');
    } else {
      params.from_date = moment().subtract(3, 'months').format('YYYY-MM-DD');
      params.to_date = moment().format('YYYY-MM-DD');
    }

    return params;
  };

  @action changeFilters = ({ search, branches, dateFrom, dateTo }: Partial<OrdersRatingFilter>) => {
    if (typeof search === 'string') {
      this.filter.search = search;
      this._getOrdersRatingWithDelay();
    }

    if (branches) {
      const allBranchesOptionWasNotChanged = !!findAllSelectedOption(branches) === !!findAllSelectedOption(this.filter.branches);
      const allBranchesOptionWasSelected = !!findAllSelectedOption(branches) && !findAllSelectedOption(this.filter.branches);
      const allBranchesOptionWasUnselected = !findAllSelectedOption(branches) && !!findAllSelectedOption(this.filter.branches);
      const someOptionWasUnselected = allBranchesOptionWasNotChanged && branches.length !== this.filter.branches.length;
      const allAvailableOptionsWasSelected =
        allBranchesOptionWasNotChanged && !findAllSelectedOption(branches) && branches.length === this.availableBranches.length - 1;

      if (allBranchesOptionWasSelected || allAvailableOptionsWasSelected) {
        this.filter.branches = [...this.availableBranches];
      } else if (allBranchesOptionWasUnselected) {
        this.filter.branches = [];
      } else if (someOptionWasUnselected) {
        this.filter.branches = skipAllSelectedOption(branches);
      } else {
        this.filter.branches = branches;
      }
      // get order on picker closed
    }

    if (dateFrom && dateTo) {
      this.filter.dateFrom = dateFrom;
      this.filter.dateTo = dateTo;
      this._getOrdersRatingWithNoDelay();
    }
  };

  @action changeSort = ({ sortField, sortDir }: Partial<OrdersRatingSorting>) => {
    if (sortField && sortDir) {
      this.sorting = { sortField, sortDir };
      this._getOrdersRatingWithNoDelay();
    }
  };

  @action clearFilters = () => {
    this.filter = defaultFilter;
  };

  @computed get isNoOrdersRatingFound() {
    return [LoadingStatus.Success, LoadingStatus.Error].includes(this.loadingStatus) && this.ordersRatingList.length === 0;
  }

  @action public exportDocument = flow(function* (this: OrdersRatingStore): any {
    this.exportLoadingStatus = LoadingStatus.Loading;

    this.ordersExportAbortController.abort();
    const ordersExportAbortController = new AbortController();
    this.ordersExportAbortController = ordersExportAbortController;

    try {
      const config: AxiosRequestConfig = {
        signal: ordersExportAbortController.signal,
        params: this._makeFetchOrdersRatingParams(1, window.IS_MOBILE ? 'url_to_excel' : 'excel'),
        responseType: window.IS_MOBILE ? 'text' : 'blob',
      };
      const { data, headers } = yield client.get('/v3/crm/orders-rating', config, true);

      if (window.IS_MOBILE) {
        yield wms.downloadFile(data);
      } else {
        yield downloadFile(data, extractFileName(headers).replace(/\"/g, ''));
      }

      this.exportLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (ordersExportAbortController.signal.aborted) {
        this.exportLoadingStatus = LoadingStatus.Success;
      } else {
        this.exportLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'ordersHistoryStore downloadExportedDocument' });
      }
    }
  }).bind(this);
}

export const ordersRatingStore = new OrdersRatingStore();
